import { useState, useEffect, useCallback } from 'react';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { Spinner } from '../../components/ui/Spinner';

const GalleryPage = () => {
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState('all');
  const [error, setError] = useState(null);

  const fetchImages = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);

      const galleryRef = ref(storage, 'gallery');
      const albums = await listAll(galleryRef);

      const albumsData = {};

      // Fetch images from each album
      await Promise.all(
        albums.prefixes.map(async (albumRef) => {
          const albumName = albumRef.name;
          const albumImages = await listAll(albumRef);

          // Get URLs for all images in the album
          const imageUrls = await Promise.all(
            albumImages.items.map(async (imageRef) => {
              try {
                const url = await getDownloadURL(imageRef);
                return {
                  url,
                  name: imageRef.name,
                  album: albumName,
                };
              } catch (err) {
                console.warn(`Failed to load image ${imageRef.name}:`, err);
                return null;
              }
            }),
          );

          // Filter out any failed image loads
          albumsData[albumName] = imageUrls.filter(Boolean);
        }),
      );

      setImages(albumsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching gallery images:', err);

      // Retry logic for temporary failures
      if (retryCount < 3) {
        console.log(`Retrying... Attempt ${retryCount + 1}`);
        setTimeout(() => fetchImages(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }

      setError('Failed to load gallery images. Please try again later.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => fetchImages()}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          Retry Loading
        </button>
      </div>
    );
  }

  const allImages = Object.values(images).flat();
  const displayImages =
    selectedAlbum === 'all' ? allImages : images[selectedAlbum] || [];

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
            Our Gallery
          </h1>
          <p className="text-xl text-gray-600">
            Explore our collection of beautiful moments and transformations
          </p>
        </div>

        {/* Album Selection */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex rounded-md shadow-sm">
            <button
              onClick={() => setSelectedAlbum('all')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                selectedAlbum === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              All Photos
            </button>
            {Object.keys(images).map((album) => (
              <button
                key={album}
                onClick={() => setSelectedAlbum(album)}
                className={`px-4 py-2 text-sm font-medium border-l border-gray-200 ${
                  selectedAlbum === album
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${
                  album === Object.keys(images)[Object.keys(images).length - 1]
                    ? 'rounded-r-md'
                    : ''
                }`}
              >
                {album.charAt(0).toUpperCase() + album.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {displayImages.map((image, index) => (
            <div
              key={image.url}
              className={`relative group rounded-lg overflow-hidden ${
                index % 5 === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
            >
              <img
                src={image.url}
                alt={image.name}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-300 flex items-center justify-center">
                <p className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {image.name.split('.')[0]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GalleryPage;
